import { TeamId } from "@custom-types/base-types";
import { SdbTeam } from "@custom-types/teams-types";
import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { companyTeamsAsMemberSelector } from "@store/members/members-selector";
import { RootState } from "@store/store-helper";
import { teamAdapter } from "@store/teams/teams-slice";
import { TeamState } from "@store/teams/teams-slice-types";

/** Return the list of teams. */
export const teamsSelector: (state: RootState) => SdbTeam[] = createSelector(
  (state: RootState) => state.teams,
  companyTeamsAsMemberSelector,
  (state: RootState) => state.ui.search.debouncedSearchText,
  (teams, companyTeamsAsMember, searchText) => {
    const teamsList = teamAdapter.getSelectors().selectAll(teams);
    const searchTextLower = searchText.toLowerCase();
    const sdbTeams: SdbTeam[] = teamsList
      .filter((team) => team.name.toLowerCase().includes(searchTextLower))
      .map((team) => {
        const teamAsMember = companyTeamsAsMember.find(
          (teamAsMember) => teamAsMember.identity === team.id
        );
        return {
          ...team,
          role: teamAsMember?.role ?? "MEMBER",
        };
      });

    return sdbTeams.sort(
      (teamA, teamB) =>
        new Date(teamB.modifiedAt).getTime() -
        new Date(teamA.modifiedAt).getTime()
    );
  }
);

/**
 * Gets a team entity by providing the team ID
 */
export function getTeamByIdSelector(
  teamId?: TeamId
): (state: RootState) => FaroCoreApiTypes.Team | undefined {
  return createSelector(
    (state: RootState) => state.teams,
    (teams) => {
      if (!teamId) {
        return undefined;
      }
      return teamAdapter.getSelectors().selectById(teams, teamId);
    }
  );
}

/**
 * Get the selected team using the selected team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store.
 */
export const selectedTeamIdSelector: (
  state: RootState
) => FaroCoreApiTypes.Team | null = createSelector(
  (state: RootState) => state.teams,
  (teams) => {
    const selectedTeamId = teams.selectedTeam.id;
    if (!selectedTeamId) {
      return null;
    }

    return teamAdapter.getSelectors().selectById(teams, selectedTeamId) ?? null;
  }
);

/**
 * Get the selected team using the selected team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store,
 * including the user's role in the team if available.
 */
export const selectedTeamWithRoleSelector: (
  state: RootState
) => SdbTeam | null = createSelector(
  (state: RootState) => state.teams,
  companyTeamsAsMemberSelector,
  (teams, companyTeamsAsMember) => {
    const selectedTeamId = teams.selectedTeam.id;
    if (!selectedTeamId) {
      return null;
    }

    const team =
      teamAdapter.getSelectors().selectById(teams, selectedTeamId) ?? null;

    if (!team) {
      return null;
    }

    const teamAsMember = companyTeamsAsMember.find(
      (teamAsMember) => teamAsMember.identity === team.id
    );

    return {
      ...team,
      role: teamAsMember?.role ?? "MEMBER",
    };
  }
);

/**
 * Get the team using the team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store,
 * including the user's role in the team if available.
 */
export function teamWithRoleByIdSelector(
  teamId?: TeamId
): (state: RootState) => SdbTeam | null {
  return createSelector(
    (state: RootState) => state.teams,
    companyTeamsAsMemberSelector,
    (teams, companyTeamsAsMember) => {
      if (!teamId) {
        return null;
      }

      const team = teamAdapter.getSelectors().selectById(teams, teamId) ?? null;

      if (!team) {
        return null;
      }

      const teamAsMember = companyTeamsAsMember.find(
        (teamAsMember) => teamAsMember.identity === team.id
      );

      return {
        ...team,
        role: teamAsMember?.role ?? "MEMBER",
      };
    }
  );
}

/**
 * Returns the fetching properties of the teams slice.
 */
export const fetchingTeamsFlagsSelector: (
  state: RootState
) => TeamState["fetching"] = createSelector(
  (state: RootState) => state.teams,
  (teams) => {
    return teams.fetching;
  }
);

/**
 * Returns the members of the selected team.
 */
export const selectedTeamMembersSelector: (
  state: RootState
) => FaroCoreApiTypes.TeamMember[] = createSelector(
  (state: RootState) => state.teams,
  (teams) => {
    return Object.values(teams.selectedTeam.members);
  }
);

/** Returns the selected team as member type */
export const selectedTeamAsMemberSelector: (
  state: RootState
) => FaroCoreApiTypes.CompanyMemberDetails | null = createSelector(
  (state: RootState) => state.teams,
  (teams) => {
    return teams.selectedTeam.teamAsMember;
  }
);

/** Returns the total count of members in the selected team */
export const selectedTeamTotalCountSelector: (state: RootState) => number =
  createSelector(
    (state: RootState) => state.teams,
    (teams) => {
      return teams.selectedTeam.totalCount;
    }
  );

/**
 * Returns the next team member ID if set.
 */
export const nextTeamMemberSelector: (state: RootState) => string | null =
  createSelector(
    (state: RootState) => state.teams,
    (teams) => {
      return teams.nextTeamMember;
    }
  );
